// USER-LANG-LEFTOVERS-1 — ONE language-generic "time ago" helper for the whole client.
//
// Why this exists: cards built their own relative time from template literals
// (`${days}d ago`, "today", `fromNow(true) + " ago"`), so the age stayed English after the
// profile language changed. Every compact age now goes through the `common:relativeTime.*`
// keys, which exist in every locale (sk, en, cs, de, it, pl).
//
// Long-form relative time ("3 months ago") keeps using dayjs `fromNow()` — dayjs follows
// the global locale that core/i18n.ts sets on every `languageChanged`, so it is already
// translated. This file is for the COMPACT form only (cards, chips, rows).
//
// Pure functions: `translate` is injectable (tests, or a namespaced hook `translate`); the
// default reads the i18next singleton that core/i18n.ts initialises. Importing `i18next`
// directly (not core/i18n) keeps the module free of the HTTP-backend side effects.
import i18next from "i18next";

export type Translate = (key: string, options?: Record<string, unknown>) => string;

const defaultTranslate: Translate = (key, options) => i18next.t(key, options) as string;

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const toTime = (date: string | number | Date): number =>
  date instanceof Date ? date.getTime() : new Date(date).getTime();

/**
 * Compact age of `date` relative to `now`: "just now" / "5 min ago" / "3h ago" / "103d ago"
 * (EN), "adesso" / "5 min fa" / "3 h fa" / "103 g fa" (IT), and so on.
 * Returns "" for a missing or unparseable date. Future dates read as "just now".
 */
export const formatTimeAgo = (
  date: string | number | Date | null | undefined,
  translate: Translate = defaultTranslate,
  now: Date = new Date()
): string => {
  if (date === null || date === undefined || date === "") return "";
  const then = toTime(date);
  if (Number.isNaN(then)) return "";
  const diff = now.getTime() - then;
  if (diff < MINUTE_MS) return translate("common:relativeTime.justNow");
  if (diff < HOUR_MS) return translate("common:relativeTime.minutesAgo", { count: Math.floor(diff / MINUTE_MS) });
  if (diff < DAY_MS) return translate("common:relativeTime.hoursAgo", { count: Math.floor(diff / HOUR_MS) });
  return translate("common:relativeTime.daysAgo", { count: Math.floor(diff / DAY_MS) });
};

/** Whole-day age for day-granular cards: 0 -> "today", N -> "Nd ago" (localised). */
export const formatDaysAgo = (days: number, translate: Translate = defaultTranslate): string =>
  days <= 0
    ? translate("common:relativeTime.today")
    : translate("common:relativeTime.daysAgo", { count: Math.floor(days) });

/** The active UI language for `Intl` / `toLocale*String` calls — never the browser default. */
export const getUiLocale = (): string => i18next.language || "en";
